import { useEffect, useState } from 'react';

import { retryMethodByLinePay } from '../../../constants/payby';
import { translate } from '../../../i18n';
import { LinePayPrime } from '../../../types/LinePayPrime';
import { PaymentMethodFromApi } from '../../../types/Payment';
import { PaymentData } from '../../../types/TransactionDetail';
import { useRoute } from '../../../utils/useRoute';
import S from './style';
import utils from './utils';

type LinePayInfo = Pick<
  PaymentMethodFromApi,
  'text' | 'maintaining_text' | 'is_allowed' | 'hint'
>;

interface PropsPaymentData extends PaymentData {
  PAY_NAME: string;
  created_at: number;
  transaction_hint: string;
  transaction_no: string;
  transaction_status: string;
}

interface Props {
  getLinePayPrime: (() => Promise<{ prime: string }>) | null;
  isLinePayAvailable: () => Promise<boolean>;
  linePayInfo: LinePayInfo | undefined;
  paymentData: PropsPaymentData;
  postLinePayPrime: (_args: {
    failure?: Function;
    prime: string;
    transactionID: number;
    success: Function;
  }) => void;
  setDirectPayModalState: (_state: boolean) => void;
  showFullScreenLoading: Function;
  transactionID: number;
  updateApiCommonError: (_args: {
    contentText: string;
    positiveText: string;
    negativeText?: string;
  }) => void;
}

export function handleLinePay({
  getLinePayPrime,
  isLinePayAvailable,
  maintainingText,
  onError,
  postLinePayPrime,
  resolve,
  transactionID,
  routeName,
}) {
  return Promise.all([
    isLinePayAvailable(),
    getLinePayPrime({ transactionID, scene: routeName }),
  ])
    .then(([isClientSupportLinePay, { prime }]) => {
      if (isClientSupportLinePay && prime) {
        postLinePayPrime({
          failure: () => {
            onError(translate('api-common-error-msg-try-again'));
          },
          prime,
          success: (data: LinePayPrime) => {
            const isValidUrl = data.redirect_url && data.redirect_url.length;

            // redirect_url may empty string even success
            if (data.status && isValidUrl) {
              utils.onPaid(transactionID, data);
              resolve();
            } else {
              onError(translate('api-common-error-msg-try-again'));
            }
          },
          transactionID,
          screen: routeName,
        });
      } else {
        onError(maintainingText);
      }
    })
    .catch(() => {
      onError(maintainingText);
    });
}

export default function RetryLinePayBtn({
  getLinePayPrime,
  isLinePayAvailable,
  linePayInfo,
  paymentData,
  postLinePayPrime,
  showFullScreenLoading,
  transactionID,
  updateApiCommonError,
}: Props) {
  const [btnState, setBtnState] = useState({
    isShown: false,
    couldRetry: false,
  });

  useEffect(() => {
    if (paymentData) {
      const isShown = retryMethodByLinePay(paymentData.PAY_NAME).length > 0;
      // Note:
      // props.TapPay from withTapPay may be undefined though loadSuccess
      const couldRetry = !!getLinePayPrime && isShown;

      setBtnState({
        isShown,
        couldRetry,
      });
    }
  }, [paymentData, getLinePayPrime]);

  const { name: routeName } = useRoute();
  const waitPostLinePayPrime = () => {
    return new Promise<void>((resolve, reject) => {
      const onError = contentText => {
        reject();

        updateApiCommonError({
          contentText,
          positiveText: translate('alert-default-positive-text'),
        });
      };
      const maintainingText =
        linePayInfo?.maintaining_text || translate('line-pay-payment-failure');

      if (linePayInfo && getLinePayPrime) {
        handleLinePay({
          getLinePayPrime,
          isLinePayAvailable,
          postLinePayPrime,
          maintainingText,
          onError,
          resolve,
          transactionID,
          routeName,
        }).catch(() => {
          // @ts-ignore
        });
      } else {
        onError(maintainingText);
      }
    });
  };

  const onPress = () => {
    showFullScreenLoading({
      waitForClose: waitPostLinePayPrime(),
    });
  };

  if (!btnState.isShown) {
    return null;
  }

  return (
    <S.RetryLinePayBtn
      accessibilityRole="button"
      disabled={!btnState.couldRetry}
      onPress={onPress}
    >
      {btnState.couldRetry ? (
        <S.RetryLinePayText>
          {translate('orderDetail-retry-line-pay')}
        </S.RetryLinePayText>
      ) : (
        <S.RetryLinePayLoading size="small" />
      )}
    </S.RetryLinePayBtn>
  );
}
